import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
// Badge component is imported but not used in this file
// import { Badge } from '@/components/ui/badge';
import { WordUsageDocument } from '../types/wordUsage';
import { Button } from '@/components/ui/button';
import { ArrowUpDown, ArrowUp, ArrowDown, Trash2, Ban } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-sonner-toast';

interface EnhancedWordTableProps {
  words: WordUsageDocument[];
  title: string;
  onWordDeleted?: () => void; // Callback to refresh data after deletion
}

type SortDirection = 'asc' | 'desc' | null;
type SortField =
  | 'word'
  | 'wordLength'
  | 'outputTotal'
  | 'inputTotal'
  | 'itemRatio'
  | 'locationRatio'
  | 'characterRatio'
  | 'deconstructorRatio';

const EnhancedWordTable: React.FC<EnhancedWordTableProps> = ({ words, title, onWordDeleted }) => {
  const [sortField, setSortField] = useState<SortField>('word');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [wordToDelete, setWordToDelete] = useState<string>('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [banDialogOpen, setBanDialogOpen] = useState(false);
  const [wordToBan, setWordToBan] = useState<string>('');
  const [isBanning, setIsBanning] = useState(false);
  const { toast } = useToast();

  // Handle ban word confirmation
  const handleBanClick = (word: string) => {
    setWordToBan(word);
    setBanDialogOpen(true);
  };

  // Handle actual word banning
  const handleBanConfirm = async () => {
    if (!wordToBan) return;

    setIsBanning(true);
    try {
      // Import the ban function dynamically to avoid circular dependencies
      const { banWord } = await import('../lib/bannedWords');

      await banWord(wordToBan, 'Banned from Word Analytics');

      toast({
        title: 'Success',
        description: `Word "${wordToBan}" has been banned and will be excluded from AI responses.`,
        variant: 'default',
      });
    } catch (error) {
      console.error('Error banning word:', error);
      toast({
        title: 'Error',
        description: `Failed to ban word "${wordToBan}". Please try again.`,
        variant: 'destructive',
      });
    } finally {
      setIsBanning(false);
      setBanDialogOpen(false);
      setWordToBan('');
    }
  };

  // Handle delete word confirmation
  const handleDeleteClick = (word: string) => {
    setWordToDelete(word);
    setDeleteDialogOpen(true);
  };

  // Handle actual word deletion
  const handleDeleteConfirm = async () => {
    if (!wordToDelete) return;

    setIsDeleting(true);
    try {
      // Import the delete function dynamically to avoid circular dependencies
      const { deleteWordCompletely } = await import('../lib/admin-api');

      await deleteWordCompletely(wordToDelete);

      toast({
        title: 'Success',
        description: `Word "${wordToDelete}" has been completely removed from all collections.`,
        variant: 'default',
      });

      // Refresh the data
      if (onWordDeleted) {
        onWordDeleted();
      }
    } catch (error) {
      console.error('Error deleting word:', error);
      toast({
        title: 'Error',
        description: `Failed to delete word "${wordToDelete}". Please try again.`,
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setWordToDelete('');
    }
  };

  // Process the data to create the enhanced table structure
  const processedData = useMemo(() => {
    if (!words || !Array.isArray(words)) {
      return [];
    }

    return words.map((word) => {
      // Get input and output counts
      const outputTotal = word.outputCount || 0;
      const inputTotal = word.inputCount || 0;

      // Get forge-specific counts
      const itemOutput = word.itemForgeCount || 0;
      const locationOutput = word.locationForgeCount || 0;
      const characterOutput = word.characterForgeCount || 0;
      const deconstructorOutput = word.deconstructorOutputCount || 0;

      // Calculate input counts for each forge type
      // This is an approximation since we don't have direct access to forge-specific input counts
      let itemInput = 0;
      let locationInput = 0;
      let characterInput = 0;
      let deconstructorInput = 0;

      // If we have inputToOutputs data, we can calculate forge-specific input counts
      if (word.inputToOutputs && typeof word.inputToOutputs === 'object') {
        Object.values(word.inputToOutputs).forEach((connection: any) => {
          if (connection && typeof connection === 'object' && connection.forgeTypes) {
            if (connection.forgeTypes.Item) itemInput += connection.forgeTypes.Item;
            if (connection.forgeTypes.Location) locationInput += connection.forgeTypes.Location;
            if (connection.forgeTypes.Character) characterInput += connection.forgeTypes.Character;
            if (connection.forgeTypes.Deconstructor)
              deconstructorInput += connection.forgeTypes.Deconstructor;
          }
        });
      }

      // Calculate I/O ratios
      const itemRatio =
        itemOutput > 0 && itemInput > 0 ? parseFloat((itemInput / itemOutput).toFixed(2)) : 0;

      const locationRatio =
        locationOutput > 0 && locationInput > 0
          ? parseFloat((locationInput / locationOutput).toFixed(2))
          : 0;

      const characterRatio =
        characterOutput > 0 && characterInput > 0
          ? parseFloat((characterInput / characterOutput).toFixed(2))
          : 0;

      const deconstructorRatio =
        deconstructorOutput > 0 && deconstructorInput > 0
          ? parseFloat((deconstructorInput / deconstructorOutput).toFixed(2))
          : 0;

      return {
        word: word.word,
        wordLength: word.word.length,
        outputTotal,
        inputTotal,
        itemInput,
        itemOutput,
        locationInput,
        locationOutput,
        characterInput,
        characterOutput,
        deconstructorInput,
        deconstructorOutput,
        // Keep the ratios for sorting purposes
        itemRatio,
        locationRatio,
        characterRatio,
        deconstructorRatio,
      };
    });
  }, [words]);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm) return processedData;

    return processedData.filter((item) =>
      item.word.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [processedData, searchTerm]);

  // Sort data based on sort field and direction
  const sortedData = useMemo(() => {
    if (!sortField || !sortDirection) return filteredData;

    return [...filteredData].sort((a, b) => {
      if (sortDirection === 'asc') {
        return a[sortField] > b[sortField] ? 1 : -1;
      } else {
        return a[sortField] < b[sortField] ? 1 : -1;
      }
    });
  }, [filteredData, sortField, sortDirection]);

  // Handle sort click
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection('asc');
      } else {
        setSortDirection('asc');
      }
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Render sort icon
  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-2 h-4 w-4" />;
    }

    return sortDirection === 'asc' ? (
      <ArrowUp className="ml-2 h-4 w-4" />
    ) : (
      <ArrowDown className="ml-2 h-4 w-4" />
    );
  };

  return (
    <div className="rounded-md border">
      <div className="p-4 border-b">
        <h3 className="text-lg font-medium mb-2">{title}</h3>
        <Input
          placeholder="Search words..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-sm"
        />
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('word')}
                  className="font-semibold"
                >
                  Word {renderSortIcon('word')}
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('wordLength')}
                  className="font-semibold"
                >
                  Length {renderSortIcon('wordLength')}
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('outputTotal')}
                  className="font-semibold"
                >
                  Output Total {renderSortIcon('outputTotal')}
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('inputTotal')}
                  className="font-semibold"
                >
                  Input Total {renderSortIcon('inputTotal')}
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('itemRatio')}
                  className="font-semibold"
                >
                  Item I/O {renderSortIcon('itemRatio')}
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('locationRatio')}
                  className="font-semibold"
                >
                  Location I/O {renderSortIcon('locationRatio')}
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('characterRatio')}
                  className="font-semibold"
                >
                  Character I/O {renderSortIcon('characterRatio')}
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('deconstructorRatio')}
                  className="font-semibold"
                >
                  Deconstructor I/O {renderSortIcon('deconstructorRatio')}
                </Button>
              </TableHead>
              <TableHead className="text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedData.map((item, index) => (
              <TableRow key={index}>
                <TableCell className="font-medium">{item.word}</TableCell>
                <TableCell>{item.wordLength}</TableCell>
                <TableCell>{item.outputTotal}</TableCell>
                <TableCell>{item.inputTotal}</TableCell>
                <TableCell>
                  {item.itemInput > 0 || item.itemOutput > 0
                    ? `${item.itemInput}/${item.itemOutput}`
                    : '-'}
                </TableCell>
                <TableCell>
                  {item.locationInput > 0 || item.locationOutput > 0
                    ? `${item.locationInput}/${item.locationOutput}`
                    : '-'}
                </TableCell>
                <TableCell>
                  {item.characterInput > 0 || item.characterOutput > 0
                    ? `${item.characterInput}/${item.characterOutput}`
                    : '-'}
                </TableCell>
                <TableCell>
                  {item.deconstructorInput > 0 || item.deconstructorOutput > 0
                    ? `${item.deconstructorInput}/${item.deconstructorOutput}`
                    : '-'}
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleBanClick(item.word)}
                      className="h-8 w-8 p-0 text-orange-600 hover:text-orange-700 hover:bg-orange-50 dark:hover:bg-orange-950"
                      title={`Ban "${item.word}" from AI responses`}
                    >
                      <Ban className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteClick(item.word)}
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                      title={`Delete "${item.word}" completely`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {sortedData.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-4 text-muted-foreground">
                  No data found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Ban Confirmation Dialog */}
      <Dialog open={banDialogOpen} onOpenChange={setBanDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ban Word from AI Responses</DialogTitle>
            <DialogDescription>
              Are you sure you want to ban the word "{wordToBan}"?
              <br />
              <br />
              <strong>This action will:</strong>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Add the word to the banned words list</li>
                <li>Prevent the word from appearing in any AI responses</li>
                <li>Apply to all forges (Item, Location, Character) and deconstructor</li>
                <li>Take effect immediately for new AI requests</li>
              </ul>
              <br />
              <em>
                Note: This does not remove existing instances of the word from user game states.
              </em>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBanDialogOpen(false)} disabled={isBanning}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleBanConfirm} disabled={isBanning}>
              {isBanning ? 'Banning...' : 'Ban Word'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Word Completely</DialogTitle>
            <DialogDescription>
              Are you sure you want to completely delete the word "{wordToDelete}"?
              <br />
              <br />
              <strong>This action will:</strong>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Remove the word from the word_usage collection</li>
                <li>
                  Remove all references from inputToOutputs and outputFromInputs in other words
                </li>
                <li>
                  Remove the word from all user game states (elements, discoveries, combinations,
                  deconstructions, wordHistory)
                </li>
                <li>This action cannot be undone</li>
              </ul>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} disabled={isDeleting}>
              {isDeleting ? 'Deleting...' : 'Delete Permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EnhancedWordTable;
